import type { ReactNode } from "react";

import { cn } from "@/lib/cn";

/**
 * The table shell.
 *
 * Tables are not a fallback here — for administration and inventory they are the
 * right tool, and the spec says so (§7, §36). The shell owns the horizontal
 * scroll so a wide table never slides the whole application sideways.
 *
 * It is a safety net rather than the plan: the columns hide themselves as the
 * container narrows, so a table that scrolls sideways means a screen that has
 * not been given enough breakpoints yet.
 */
export function TableShell({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("overflow-x-auto", className)}>
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  );
}

export function Th({
  children,
  align = "left",
  className,
  ...props
}: React.ThHTMLAttributes<HTMLTableCellElement> & { align?: "left" | "right" | "center" }) {
  return (
    <th
      scope="col"
      className={cn(
        // Not sticky. `overflow-x: auto` above also makes this a vertical scroll
        // container, so a sticky header would pin itself to a box that never
        // scrolls — it would look right and do nothing. One page-level scroll is
        // worth more than a header that only pretends to follow.
        "whitespace-nowrap border-b border-border bg-surface px-4 py-2.5",
        "text-[11px] font-semibold uppercase tracking-wide text-ink-subtle",
        align === "right" && "text-right",
        align === "center" && "text-center",
        align === "left" && "text-left",
        className,
      )}
      {...props}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  align = "left",
  className,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement> & { align?: "left" | "right" | "center" }) {
  return (
    <td
      className={cn(
        "border-b border-border/60 px-4 py-row align-middle text-ink",
        align === "right" && "text-right",
        align === "center" && "text-center",
        className,
      )}
      {...props}
    >
      {children}
    </td>
  );
}

export function Tr({
  children,
  onClick,
  className,
}: {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <tr
      onClick={onClick}
      className={cn(
        "transition-colors hover:bg-surface-hover",
        onClick && "cursor-pointer",
        className,
      )}
    >
      {children}
    </tr>
  );
}

/** Pagination that says where you are, not just which page. */
export function Pagination({
  offset,
  limit,
  total,
  onChange,
}: {
  offset: number;
  limit: number;
  total: number;
  onChange: (offset: number) => void;
}) {
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + limit, total);
  const canPrevious = offset > 0;
  const canNext = offset + limit < total;

  return (
    <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-3 text-[13px] text-ink-muted">
      <span className="tnum">
        {from}–{to} sur {total}
      </span>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={!canPrevious}
          onClick={() => onChange(Math.max(0, offset - limit))}
          className="rounded-md border border-border px-3 py-1.5 transition-colors hover:bg-surface-hover disabled:opacity-40 disabled:hover:bg-transparent"
        >
          Précédent
        </button>
        <button
          type="button"
          disabled={!canNext}
          onClick={() => onChange(offset + limit)}
          className="rounded-md border border-border px-3 py-1.5 transition-colors hover:bg-surface-hover disabled:opacity-40 disabled:hover:bg-transparent"
        >
          Suivant
        </button>
      </div>
    </div>
  );
}
